<template>
  <div class="app-container superviseur-dashboard">
    <div class="page-title">
      <h2>{{ $t('superviseur.dashboard.title') }}</h2>
    </div>

    <el-alert
      v-if="dashboard && !dashboard.signature_enregistree"
      :title="$t('superviseur.dashboard.signatureMissing')"
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 20px;"
    >
      <router-link to="/espace-superviseur/profil">{{ $t('superviseur.dashboard.addSignature') }}</router-link>
    </el-alert>

    <div class="filter-container" style="margin-bottom: 20px;">
      <el-select
        v-model="query.campagne_agricole_id"
        class="filter-item"
        style="width: 220px;"
        :placeholder="$t('recolte.campagne')"
        @change="loadDashboard"
      >
        <el-option
          v-for="campagne in campagnes"
          :key="campagne.id"
          :label="campagne.nom"
          :value="campagne.id"
        />
      </el-select>
    </div>

    <el-row v-loading="loading" :gutter="20">
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="hover" class="kpi-card" @click.native="goTo('ci')">
          <div class="kpi-value">{{ counts.ci }}</div>
          <div class="kpi-label">{{ $t('superviseur.dashboard.ciAValider') }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="hover" class="kpi-card" @click.native="goTo('formation')">
          <div class="kpi-value">{{ counts.formations }}</div>
          <div class="kpi-label">{{ $t('superviseur.dashboard.formationsAValider') }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="hover" class="kpi-card" @click.native="goTo('audit')">
          <div class="kpi-value">{{ counts.audits }}</div>
          <div class="kpi-label">{{ $t('superviseur.dashboard.auditsAValider') }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="hover" class="kpi-card" @click.native="goTo('parcelle')">
          <div class="kpi-value">{{ counts.parcelles }}</div>
          <div class="kpi-label">{{ $t('superviseur.dashboard.parcellesAValider') }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" style="margin-top: 20px;">
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="never">
          <div slot="header">{{ $t('superviseur.dashboard.validesMois') }}</div>
          <p>CI : <strong>{{ valides.ci }}</strong></p>
          <p>{{ $t('superviseur.types.formation') }} : <strong>{{ valides.formations }}</strong></p>
          <p>{{ $t('superviseur.types.audit') }} : <strong>{{ valides.audits }}</strong></p>
          <p>{{ $t('superviseur.types.parcelle') }} : <strong>{{ valides.parcelles }}</strong></p>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="never">
          <div slot="header">{{ $t('superviseur.dashboard.equipe') }}</div>
          <p class="kpi-value kpi-value--small">{{ equipeCount }}</p>
          <p>{{ $t('superviseur.dashboard.controleursActifs') }}</p>
          <el-button type="text" @click="$router.push('/espace-superviseur/equipe')">
            {{ $t('superviseur.dashboard.voirEquipe') }}
          </el-button>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="never">
          <div slot="header">{{ $t('superviseur.dashboard.actions') }}</div>
          <el-button type="primary" @click="$router.push('/espace-superviseur/a-valider')">
            {{ $t('superviseur.dashboard.ouvrirAValider') }}
          </el-button>
          <el-button @click="$router.push('/espace-superviseur/rapport')">
            {{ $t('superviseur.dashboard.ouvrirRapport') }}
          </el-button>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import Resource from '@/api/resource';
import { getSuperviseurDashboard } from '@/api/superviseur';

const campagneResource = new Resource('campagne_agricoles');

export default {
  name: 'SuperviseurDashboard',
  data() {
    return {
      loading: false,
      dashboard: null,
      campagnes: [],
      query: {
        campagne_agricole_id: null,
      },
    };
  },
  computed: {
    counts() {
      const a = this.dashboard && this.dashboard.a_valider ? this.dashboard.a_valider : {};
      return {
        ci: a.ci || 0,
        formations: a.formations || 0,
        audits: a.audits || 0,
        parcelles: a.parcelles || 0,
      };
    },
    valides() {
      const v = this.dashboard && this.dashboard.valides_mois ? this.dashboard.valides_mois : {};
      return {
        ci: v.ci || 0,
        formations: v.formations || 0,
        audits: v.audits || 0,
        parcelles: v.parcelles || 0,
      };
    },
    equipeCount() {
      return this.dashboard ? (this.dashboard.equipe_count || 0) : 0;
    },
  },
  created() {
    this.loadCampagnes();
  },
  methods: {
    async loadCampagnes() {
      const { data } = await campagneResource.list({ limit: 100 });
      this.campagnes = data || [];
      try {
        const { data: current } = await campagneResource.sublist('/en_cours');
        this.query.campagne_agricole_id = current && current.id
          ? current.id
          : (this.campagnes[0] ? this.campagnes[0].id : null);
      } catch (error) {
        this.query.campagne_agricole_id = this.campagnes[0] ? this.campagnes[0].id : null;
      }
      this.loadDashboard();
    },
    async loadDashboard() {
      this.loading = true;
      try {
        const response = await getSuperviseurDashboard(this.query);
        this.dashboard = response.data || null;
      } catch (error) {
        console.error(error);
        this.$message.error(this.$t('general.errorOccurred'));
      } finally {
        this.loading = false;
      }
    },
    goTo(type) {
      const query = {
        type,
        campagne_agricole_id: this.query.campagne_agricole_id,
      };
      this.$router.push({ path: '/espace-superviseur/a-valider', query });
    },
  },
};
</script>

<style scoped>
.kpi-card {
  cursor: pointer;
  text-align: center;
  margin-bottom: 16px;
}
.kpi-value {
  font-size: 36px;
  font-weight: 700;
  color: #409eff;
}
.kpi-value--small {
  font-size: 28px;
}
.kpi-label {
  margin-top: 8px;
  color: #606266;
}
</style>
